import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, Plus, Search, ListPlus, X, AlertTriangle, CheckCircle2, Warehouse, Settings } from 'lucide-react';
import { usePayees, useCreatePayee } from '@/hooks/use-payees';
import { useLocations } from '@/hooks/use-locations';
import { useBarcodeScan } from '@/hooks/use-barcode-scan';
import { useProducts, useUpdateProduct } from '@/hooks/use-products';
import { useCorrectConversion } from '@/hooks/use-purchase-orders';
import { useWarehouses, useCheckWarehouseAvailability } from '@/hooks/use-warehouses';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/use-permissions';
import { isPrivateWarehouseVendor, prefillLineCost } from '@/lib/private-warehouse';
import { useCanSeeCost } from '@/hooks/use-pos-config';
import { api } from '@/lib/api-client';
import { unitOptionsFor } from '@/lib/constants';
import { formatCurrency } from '@/lib/utils';
import { toast } from '@/stores/toast-store';
import { PhoneInput } from '@/components/ui/phone-input';
import { DateInput } from '@/components/ui/date-input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { validatePhone } from '@/lib/validators';
import { ProductPickerModal } from './product-picker-modal';
import { isZeroQty, ZERO_QTY_ROW_CLASS } from './zero-qty';
import { ZeroQtyTag } from './zero-qty-tag';

interface LineItem {
  // The DB id of the purchase_order_items row, when editing/viewing an existing
  // PO. Absent for newly-added rows on a draft. Required by the
  // correct-conversion endpoint, so it's only populated for received POs that
  // came back from the server.
  id?: string;
  productId: string;
  productName: string;
  productBarcode: string;
  unitOfMeasure: string;
  quantity: string;
  costPerUnit: string;
  salePrice: string;
  baseQty: string; // conversion factor: purchasing unit → selling units
  soldByWeight: boolean;
}

interface PurchaseOrderFormProps {
  initialData?: any;
  defaultLocationId?: string;
  onSubmit: (data: any) => Promise<void> | void;
  isSubmitting: boolean;
}

export function PurchaseOrderForm({ initialData, defaultLocationId, onSubmit, isSubmitting }: PurchaseOrderFormProps) {
  const { data: locationData } = useLocations();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  // A new PO needs Create; changing a saved one needs Edit (which covers
  // marking it paid and fixing conversions). Without it the form is read-only.
  const canEditPo = permissions.has('purchase_orders.edit');
  const canSavePo = initialData ? canEditPo : permissions.has('purchase_orders.create');
  const canAddVendor = permissions.hasAny('vendors.add', 'sp.payees.create');
  // Linking a vendor to a warehouse edits the vendor.
  const canEditVendor = permissions.has('sp.payees.edit');
  // Role-based cost visibility (kept intact — same hook other surfaces use).
  const canSeeCost = useCanSeeCost();
  // Product decision: hide cost/total columns in PO creation regardless of
  // role. Flip to `true` to bring them back; the role check still applies.
  const showCostColumns = false;

  const locations = locationData?.data ?? [];

  const [payeeId, setPayeeId] = useState(initialData?.payeeId || '');
  const [locationId, setLocationId] = useState(initialData?.locationId || defaultLocationId || '');

  // Vendors and products are scoped server-side by locationId. Keying both
  // queries on locationId means switching the active location refetches under
  // a fresh cache entry instead of returning stale data — this is the actual
  // fix for the "vendor list doesn't update on location switch" bug. The
  // `enabled` guard prevents a no-location query from firing on first render
  // before the location store hydrates.
  const { data: payeeData, isFetching: payeesFetching } = usePayees(
    { isActive: true, perPage: 500, locationId: locationId || undefined },
    { enabled: !!locationId },
  );
  // Tenant-wide products list — kept as a fallback only (used when no vendor
  // is selected yet, or for products that aren't in the vendor's catalog but
  // we still need name/units lookup). The inline product search below now
  // prefers vendor-scoped rows so the line item picks up the per-location
  // purchasing unit configured on the location product.
  const { data: productData, isFetching: productsFetching } = useProducts(
    { isActive: true, perPage: 500, locationId: locationId || undefined, include: 'units' },
    { enabled: !!locationId },
  );

  // Vendor-scoped product catalog from /payees/:id/products. Same endpoint
  // the picker modal uses, which means each row carries:
  //   - defaultPurchasingUnit resolved from product_locations.default_purchasing_unit
  //     for this location, falling back to products.default_purchasing_unit
  //     (tenant-wide). So when the buyer has set "case" for this product at
  //     this location, the line item unit defaults to "case" instead of "each".
  //   - units[] rows so findBaseQty resolves the qty conversion.
  // We page through everything and concat client-side so the inline search
  // can fuzzy-match across the vendor's full catalog without a per-keystroke
  // request.
  const VENDOR_PRODUCTS_PAGE_SIZE = 500;
  const vendorProductsQuery = useInfiniteQuery({
    queryKey: ['payee-products', payeeId, locationId],
    queryFn: ({ pageParam = 1 }) =>
      api.get<{ data: any[]; pagination: { page: number; perPage: number; total: number; totalPages: number } }>(
        `/payees/${payeeId}/products`,
        {
          ...(locationId ? { locationId } : {}),
          page: pageParam,
          perPage: VENDOR_PRODUCTS_PAGE_SIZE,
        },
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    enabled: !!payeeId && !!locationId,
  });
  useEffect(() => {
    if (vendorProductsQuery.hasNextPage && !vendorProductsQuery.isFetchingNextPage) {
      vendorProductsQuery.fetchNextPage();
    }
  }, [
    vendorProductsQuery.hasNextPage,
    vendorProductsQuery.isFetchingNextPage,
    vendorProductsQuery.data,
    vendorProductsQuery,
  ]);

  const allPayees = payeeData?.data ?? [];
  // Prefer the vendor-scoped catalog when we have one — that's the only place
  // the per-vendor-per-location unit override is exposed. Fall back to the
  // tenant-wide list when no vendor is selected (the search dropdown is gated
  // by payeeId+locationId in the JSX anyway, so this fallback is mainly here
  // for downstream lookups like findBaseQty during edit).
  const vendorProducts = vendorProductsQuery.data?.pages.flatMap((p) => p.data) ?? [];
  const allProducts: any[] = vendorProducts.length ? vendorProducts : (productData?.data ?? []);

  // Loading affordance: show the overlay while either of the location-scoped
  // queries is in flight. Replaces the prior 350ms timer-based heuristic so
  // the spinner reflects actual fetch state — the user always sees the new
  // location's data appear under the spinner, not stale data after a fixed
  // wait.
  const locationSwitching = payeesFetching || productsFetching;
  const createdByName = initialData?.createdByName || user?.name || '';
  const [payeeInvoiceNo, setPayeeInvoiceNo] = useState(initialData?.payeeInvoiceNo || '');
  // Status is now read-only on the form — transitions go through the dedicated
  // Send/Cancel/Receive endpoints. This state is just used for the labelled
  // display (Draft vs Sent vs Cancelled vs Received).
  const [status, setStatus] = useState(initialData?.status || 'draft');
  // Once a PO hits a terminal status (received / fulfilled / cancelled) the
  // form is locked — editing line items now would silently desync inventory
  // and the already-sent vendor email. The API also rejects PUT in these
  // statuses; this is just the UX nudge so the user doesn't bother typing.
  const statusLocked = ['received', 'fulfilled', 'cancelled'].includes(status);
  const isLocked = statusLocked || !canSavePo;
  const [orderPaid, setOrderPaid] = useState(initialData?.orderPaid || false);
  const [paidToggleSaving, setPaidToggleSaving] = useState(false);
  const orderReceived = initialData?.orderReceived || false;
  const [paymentDueDate, setPaymentDueDate] = useState(initialData?.paymentDueDate || '');
  const receivedDate = initialData?.receivedDate || '';
  const createdAt = initialData?.createdAt || new Date().toISOString();
  const [notes, setNotes] = useState(initialData?.notes || '');
  const [purpose, setPurpose] = useState(initialData?.purpose || '');
  const [vendorEmail, setVendorEmail] = useState('');

  const [items, setItems] = useState<LineItem[]>(() => {
    if (initialData?.items?.length) {
      return initialData.items.map((item: any) => ({
        id: item.id,
        productId: item.productId,
        productName: item.productName || '',
        productBarcode: item.productBarcode || '',
        unitOfMeasure: item.unitOfMeasure || 'each',
        quantity: String(item.quantity),
        costPerUnit: String(item.costPerUnit),
        salePrice: String(item.salePrice ?? ''),
        baseQty: String(item.baseQty ?? '1'),
        soldByWeight: !!item.soldByWeight,
      }));
    }
    return [];
  });

  // Vendor self-warehouse filter is now applied server-side in GET /payees
  // when locationId is supplied. Local alias kept so downstream JSX continues
  // to reference `payees` without churn.
  // When editing an existing PO, ensure the PO's vendor appears in the dropdown
  // even if it's not in the current location-scoped payee list (e.g. cross-tenant
  // warehouse vendor set by a super admin auto-PO config).
  const payees = (() => {
    if (initialData?.payeeId && !allPayees.find((p: any) => p.id === initialData.payeeId)) {
      const stub = initialData.payee
        ? { ...initialData.payee }
        : {
            id: initialData.payeeId,
            name: initialData.payeeName || 'Unknown Vendor',
            vendorType: initialData.sourceType === 'warehouse' ? 'warehouse' : null,
            email: null,
          };
      return [stub, ...allPayees];
    }
    return allPayees;
  })();

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const validate = (fields: { payeeId: string; locationId: string; purpose: string; items: LineItem[] }) => {
    const errs: Record<string, string> = {};
    if (!fields.payeeId) errs.payeeId = 'Vendor is required';
    if (!fields.locationId) errs.locationId = 'Location is required';
    if (!fields.purpose.trim())
      errs.purpose = 'Purpose is required (e.g. PRODUCE, SWEETS) — appears in the PO email subject';
    if (fields.items.length === 0) errs.items = 'At least one line item is required';
    return errs;
  };

  // Warehouse vendor detection
  const selectedPayee = payees.find((p: any) => p.id === payeeId);
  const isWarehouseVendor = selectedPayee?.vendorType === 'warehouse';
  const isPrivateWarehouse = isPrivateWarehouseVendor(selectedPayee, user?.tenantId);

  // Warehouse link/unlink inline UI state
  const { data: warehouseListData } = useWarehouses(locationId || undefined);
  const warehouseOptions = warehouseListData?.data ?? [];
  const [showLinkWarehouse, setShowLinkWarehouse] = useState(false);
  const [linkWarehouseId, setLinkWarehouseId] = useState('');
  const [linkingSaving, setLinkingSaving] = useState(false);

  // Warehouse availability check
  const checkAvailability = useCheckWarehouseAvailability();
  const [availabilityResult, setAvailabilityResult] = useState<any>(null);

  const runAvailabilityCheck = useCallback(() => {
    if (!isWarehouseVendor || !payeeId || items.length === 0) {
      setAvailabilityResult(null);
      return;
    }
    checkAvailability.mutate(
      {
        payeeId,
        locationId: locationId || undefined,
        items: items.map((item) => ({
          productId: item.productId,
          unitOfMeasure: item.unitOfMeasure,
          quantity: parseFloat(item.quantity) || 0,
        })),
      },
      {
        onSuccess: (res: any) => setAvailabilityResult(res.data),
        onError: () => setAvailabilityResult(null),
      },
    );
  }, [isWarehouseVendor, payeeId, locationId, items, checkAvailability]);

  // Debounce availability check when items change. We don't need to clear the
  // previous result here — visibleAvailabilityResult below derives a filtered
  // view of just the products still on the form, so removed lines drop out of
  // the banner immediately even though the next API call hasn't fired yet.
  const availabilityTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    clearTimeout(availabilityTimerRef.current);
    if (!isWarehouseVendor || items.length === 0) return;
    availabilityTimerRef.current = setTimeout(runAvailabilityCheck, 500);
    return () => clearTimeout(availabilityTimerRef.current);
  }, [items, isWarehouseVendor, payeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // React to location switches from the global location picker.
  // The actual loading state is derived from `payeesFetching || productsFetching`
  // (see locationSwitching above), which automatically reflects how long the
  // two location-scoped queries take to refetch under the new locationId key —
  // no fixed timer needed.
  useEffect(() => {
    // Editing an existing PO never changes its location via the global picker.
    if (initialData?.locationId) return;
    if (!defaultLocationId || defaultLocationId === locationId) return;
    // Defer the resets to a microtask so they happen AFTER the current
    // render commits — keeps the lint rule about synchronous setState in
    // effects happy without sacrificing the cascade we actually want.
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setLocationId(defaultLocationId);
      // Items reference the previous location's products; clear them so the
      // user starts fresh against the new location's catalog. Reset the payee
      // too — the vendor list filters by location, and the previously-chosen
      // vendor may not even appear under the new location.
      setItems([]);
      setPayeeId('');
      setAvailabilityResult(null);
    });
    return () => {
      cancelled = true;
    };
  }, [defaultLocationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // While the debounced check hasn't run yet, hide entries from the previous
  // result for products that are no longer on the form. Stops the banner from
  // listing a product the user just removed for the ~500ms window before the
  // re-check resolves.
  const visibleAvailabilityResult = useMemo(() => {
    if (!availabilityResult) return null;
    const presentIds = new Set(items.map((it) => it.productId));
    const filteredItems = (availabilityResult.items ?? []).filter((a: any) => presentIds.has(a.productId));
    return {
      ...availabilityResult,
      items: filteredItems,
      available: filteredItems.every((i: any) => i.sufficient),
    };
  }, [availabilityResult, items]);

  // Product search for "Add Product" combobox
  const [productSearch, setProductSearch] = useState('');
  const productScan = useBarcodeScan((code) => {
    setProductSearch(code);
    setShowProductDropdown(true);
  });
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [showPickerModal, setShowPickerModal] = useState(false);
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);

  // Set of product IDs already in the PO line items
  const existingProductIds = useMemo(() => new Set(items.map((it) => it.productId)), [items]);

  // Handle adding multiple products from the picker modal
  // Helper: find baseQty for a product's unit from its units array
  const findBaseQty = (product: any, unitOfMeasure: string): string => {
    if (!unitOfMeasure || unitOfMeasure === 'each') return '1';
    const units = product.units || [];
    const match = units.find((u: any) => u.unitName?.toLowerCase() === unitOfMeasure.toLowerCase());
    return match?.baseQty ? String(match.baseQty) : '1';
  };

  const addMultipleProducts = (selectedProducts: any[]) => {
    const newItems: LineItem[] = selectedProducts
      .filter((sp) => !existingProductIds.has(sp.id))
      .map((sp) => {
        const unit = sp.defaultPurchasingUnit || 'each';
        return {
          productId: sp.id,
          productName: sp.name,
          productBarcode: sp.primaryBarcode || '',
          unitOfMeasure: unit,
          quantity: sp.quantity || '1',
          costPerUnit: sp.costPerUnit || String(sp.cost ?? '0'),
          salePrice: String(sp.sellingPrice ?? ''),
          baseQty: findBaseQty(sp, unit),
          soldByWeight: !!sp.soldByWeight,
        };
      });
    if (newItems.length > 0) {
      setItems((prev) => [...prev, ...newItems]);
      setErrors((prev) => ({ ...prev, items: '' }));
    }
  };

  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return [];
    const q = productSearch.toLowerCase();
    return allProducts
      .filter(
        (p: any) =>
          p.name.toLowerCase().includes(q) ||
          (p.primaryBarcode && p.primaryBarcode.includes(q)) ||
          (p.sku && p.sku.toLowerCase().includes(q)),
      )
      .slice(0, 10);
  }, [productSearch, allProducts]);

  const addProduct = (product: any) => {
    // Don't add duplicates
    if (items.some((it) => it.productId === product.id)) {
      setProductSearch('');
      setShowProductDropdown(false);
      return;
    }
    const unit = product.defaultPurchasingUnit || 'each';
    setItems([
      ...items,
      {
        productId: product.id,
        productName: product.name,
        productBarcode: product.primaryBarcode || '',
        unitOfMeasure: unit,
        quantity: '1',
        costPerUnit: prefillLineCost(product, unit),
        salePrice: String(product.sellingPrice ?? ''),
        baseQty: findBaseQty(product, unit),
        soldByWeight: !!product.soldByWeight,
      },
    ]);
    setErrors((prev) => ({ ...prev, items: '' }));
    setProductSearch('');
    setShowProductDropdown(false);
  };

  const updateItem = (index: number, field: keyof LineItem, value: string) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, [field]: value };
        // When unit changes, resolve baseQty from the product's units. If the
        // chosen unit has a configured conversion, the line picks it up and
        // the inline "= N each" preview shows the correct math. If it doesn't,
        // we toast an immediate error pointing the user at the gear icon —
        // the line will already render in red and Save is disabled until the
        // conversion is set, but the toast is the in-your-face signal the
        // buyer asked for ("calculate if conversion is available, error if not").
        if (field === 'unitOfMeasure') {
          const product = allProducts.find((p: any) => p.id === item.productId);
          const resolved = product ? findBaseQty(product, value) : '1';
          updated.baseQty = resolved;
          const isEach = !value || value.toLowerCase() === 'each';
          const numericResolved = parseFloat(resolved) || 1;
          if (!isEach && numericResolved <= 1) {
            toast.error(
              `No conversion configured for "${value}" on ${item.productName}. Click the gear icon to set how many "each" are in 1 ${value}.`,
            );
          }
        }
        return updated;
      }),
    );
  };

  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const totalCost = useMemo(() => {
    return items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const cost = parseFloat(item.costPerUnit) || 0;
      return sum + qty * cost;
    }, 0);
  }, [items]);

  // Sum of (qty × baseQty) across every line — the actual per-each unit count
  // the buyer is committing to. Surfaced in the Line Items header alongside the
  // dollar total so reviewers can sanity-check conversions at a glance.
  const totalEach = useMemo(() => {
    return items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const baseQty = parseFloat(item.baseQty) || 1;
      return sum + Math.round(qty * baseQty);
    }, 0);
  }, [items]);

  // Lines whose unit isn't 'each' but baseQty is missing/1 — submitting these
  // would be silently miscounted at receive time (the bug this whole work
  // started from). We surface them inline + disable Save so the user fixes
  // the conversion before the PO ever leaves the form.
  const linesNeedingConversion = useMemo(() => {
    return items
      .map((item, index) => ({ item, index }))
      .filter(
        ({ item }) =>
          item.unitOfMeasure && item.unitOfMeasure.toLowerCase() !== 'each' && (parseFloat(item.baseQty) || 1) <= 1,
      );
  }, [items]);
  const hasMissingConversions = linesNeedingConversion.length > 0;

  // Mandatory-field gate for the Save / Update button. Mirrors the validate()
  // function above (payeeId, locationId, purpose, ≥1 line item) so the button
  // is grey-disabled until those four are populated — the user shouldn't have
  // to click Save just to learn that Purpose is required. Validation messages
  // still fire on submit attempts via setSubmitted/errors so blank fields are
  // also called out inline. The vendorMissingEmail check is folded in below
  // (it depends on selectedPayee which is declared further down).
  const requiredMissing: string[] = [];
  if (!payeeId) requiredMissing.push('Vendor');
  if (!locationId) requiredMissing.push('Location');
  if (!purpose.trim()) requiredMissing.push('Purpose');
  if (items.length === 0) requiredMissing.push('At least one line item');

  // When the user fixes a missing conversion via the product edit page in
  // another tab, returning here should pick up the new units row without a
  // manual reload. On window focus, re-fetch each affected line's product
  // detail and reconcile its baseQty if a matching unit row now exists.
  useEffect(() => {
    if (linesNeedingConversion.length === 0) return;
    const reconcile = async () => {
      const norm = (s: string) => {
        const lower = (s || '').toLowerCase().trim();
        return lower.endsWith('s') ? lower.slice(0, -1) : lower;
      };
      for (const { item, index } of linesNeedingConversion) {
        try {
          const product = await productsApiGet(item.productId);
          const target = norm(item.unitOfMeasure);
          const match = (product?.units ?? []).find((u: any) => norm(u.unitName) === target);
          const fresh = match?.baseQty ? Number(match.baseQty) : null;
          if (fresh && fresh > 1) {
            setItems((prev) => prev.map((it, i) => (i === index ? { ...it, baseQty: String(fresh) } : it)));
          }
        } catch {
          // Silent — the next focus will retry. We don't want a network blip
          // to surface as a toast on a passive refresh.
        }
      }
    };
    // Run immediately so a freshly-mounted form picks up cross-tab edits made
    // before the user came back, then keep listening for window-focus events.
    reconcile();
    window.addEventListener('focus', reconcile);
    return () => window.removeEventListener('focus', reconcile);
  }, [linesNeedingConversion]);

  // The vendor's stored email comes from the dashboard /payees record.
  // When it's missing we ask the user inline at submit time so we can both
  // dispatch the PO email AND backfill it on the vendor record.
  const vendorMissingEmail = !!selectedPayee && !selectedPayee.email && !initialData;
  if (vendorMissingEmail && !vendorEmail.trim()) requiredMissing.push('Vendor email');
  const hasRequiredMissing = requiredMissing.length > 0;

  // The inline-fix drawer opens when (a) the user clicks the per-line "Set
  // conversion" button on a row whose unit isn't configured, or (b) the server
  // rejects the create with VALIDATION_ERROR / Missing productUnits row.
  const [fixUnitFor, setFixUnitFor] = useState<{
    productId: string;
    productName: string;
    unitOfMeasure: string;
    currentBaseQty: number | null;
    itemIndex: number;
  } | null>(null);

  // The adjust-conversion drawer (admin-only, visible on received POs).
  const [adjustFor, setAdjustFor] = useState<{
    lineId: string;
    productId: string;
    productName: string;
    unitOfMeasure: string;
    quantity: number;
    oldBaseQty: number;
    itemIndex: number;
  } | null>(null);

  const submitPayload = useCallback(
    () => ({
      payeeId,
      locationId,
      createdByName: createdByName || undefined,
      payeeInvoiceNo: payeeInvoiceNo || undefined,
      vendorEmail: vendorMissingEmail ? vendorEmail.trim() : undefined,
      status,
      orderPaid,
      paymentDueDate: paymentDueDate || undefined,
      notes: notes || undefined,
      purpose: purpose.trim(),
      items: items.map((item) => ({
        productId: item.productId,
        unitOfMeasure: item.unitOfMeasure,
        quantity: parseFloat(item.quantity) || 0,
        costPerUnit: parseFloat(item.costPerUnit) || 0,
        baseQty: item.baseQty,
      })),
    }),
    [
      payeeId,
      locationId,
      createdByName,
      payeeInvoiceNo,
      vendorMissingEmail,
      vendorEmail,
      status,
      orderPaid,
      paymentDueDate,
      notes,
      purpose,
      items,
    ],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    const errs = validate({ payeeId, locationId, purpose, items });
    if (vendorMissingEmail && !vendorEmail.trim()) {
      errs.vendorEmail = 'Vendor email is required to send the PO';
    } else if (vendorMissingEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vendorEmail.trim())) {
      errs.vendorEmail = 'Enter a valid email address';
    }
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    // Hard-stop locally for missing-conversion lines so we never round-trip
    // a request the server is going to 400 — the inline banner already
    // explains the fix; the toast that would otherwise pop in the corner
    // is just noise.
    if (hasMissingConversions) {
      setErrors({});
      return;
    }
    setErrors({});
    try {
      await onSubmit(submitPayload());
    } catch (err: any) {
      // Catch the server-side "Missing productUnits row for X" 400 and surface
      // the inline-fix drawer instead of bubbling a generic error toast. The
      // server message names the unit; we find the line by its productName.
      const errCode = err?.error?.code;
      const errMsg: string = err?.error?.message || '';
      if (errCode === 'VALIDATION_ERROR' && /no unit "/i.test(errMsg)) {
        const productNameMatch = errMsg.match(/Product "([^"]+)" has no unit "([^"]+)"/);
        if (productNameMatch) {
          const [, productName, unit] = productNameMatch;
          const idx = items.findIndex((it) => it.productName === productName && it.unitOfMeasure === unit);
          const item = idx >= 0 ? items[idx] : null;
          if (item) {
            setFixUnitFor({
              productId: item.productId,
              productName: item.productName,
              unitOfMeasure: item.unitOfMeasure,
              currentBaseQty: parseFloat(item.baseQty) > 1 ? parseFloat(item.baseQty) : null,
              itemIndex: idx,
            });
            return;
          }
        }
      }
      throw err;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header Section */}
      <div className="rounded-lg border border-border bg-white p-6">
        <h3 className="mb-4 text-base font-semibold">Order Details</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* Vendor */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-foreground">
              <span>
                Vendor <span className="text-red-500">*</span>
              </span>
              {payeesFetching && (
                <span
                  className="inline-flex items-center gap-1 text-xs font-normal text-muted-foreground"
                  aria-live="polite"
                >
                  <span
                    aria-hidden="true"
                    className="h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent"
                  />
                  Reloading vendors for this location…
                </span>
              )}
            </label>
            <div className="mt-1 flex gap-1">
              <SearchableSelect
                value={payeeId}
                onChange={(next) => {
                  setPayeeId(next);
                  // Don't clear availabilityResult here — the wrapper dims the
                  // prior result via `opacity-60` while the debounced re-check
                  // is in flight, then it's replaced in place. Avoids the
                  // banner collapsing/popping during the swap.
                  setVendorEmail('');
                  if (submitted)
                    setErrors((prev) => ({
                      ...prev,
                      payeeId: next ? '' : 'Vendor is required',
                      vendorEmail: '',
                    }));
                }}
                options={payees.map((v) => ({ value: v.id, label: v.name }))}
                placeholder="Select vendor"
                searchPlaceholder="Search vendors..."
                emptyMessage="No vendors found"
                error={!!errors.payeeId}
                className="min-w-0 flex-1"
              />
              {canAddVendor && (
                <button
                  type="button"
                  onClick={() => setShowAddVendorModal(true)}
                  className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-dashed border-primary/50 px-2 text-xs font-medium text-primary hover:bg-primary/5"
                  title="Add new vendor"
                >
                  <Plus className="h-3 w-3" /> New
                </button>
              )}
            </div>
            {errors.payeeId && <p className="mt-1 text-xs text-red-500">{errors.payeeId}</p>}
            {isWarehouseVendor && (
              <div className="mt-1.5 flex items-center gap-1.5 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-700">
                <Warehouse className="h-3.5 w-3.5" />
                <span className="font-medium">
                  {isPrivateWarehouse ? 'Private Warehouse Vendor' : 'Warehouse Vendor'}
                </span>
                <span className="text-amber-600">— stock will be checked &amp; deducted</span>
                {canEditVendor && (
                  <button
                    type="button"
                    disabled={linkingSaving}
                    onClick={async () => {
                      setLinkingSaving(true);
                      try {
                        await api.put(`/payees/${payeeId}`, {
                          vendorType: 'third_party',
                          linkedWarehouseId: null,
                          linkedWarehouseLocationId: null,
                        });
                        queryClient.invalidateQueries({ queryKey: ['payees'] });
                        toast.success('Warehouse unlinked');
                      } catch {
                        toast.error('Failed to unlink warehouse');
                      } finally {
                        setLinkingSaving(false);
                      }
                    }}
                    className="ml-auto text-[10px] font-medium text-amber-800 underline hover:text-amber-950 disabled:opacity-50"
                  >
                    {linkingSaving ? 'Unlinking…' : 'Unlink'}
                  </button>
                )}
              </div>
            )}
            {/* Link warehouse — shown for third-party vendors when warehouses exist */}
            {canEditVendor && selectedPayee && !isWarehouseVendor && warehouseOptions.length > 0 && !isLocked && (
              <div className="mt-1.5">
                {!showLinkWarehouse ? (
                  <button
                    type="button"
                    onClick={() => {
                      setShowLinkWarehouse(true);
                      setLinkWarehouseId('');
                    }}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    <Warehouse className="h-3 w-3" /> Link Warehouse
                  </button>
                ) : (
                  <div className="flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-2 py-1.5">
                    <Warehouse className="h-3.5 w-3.5 shrink-0 text-primary" />
                    <select
                      value={linkWarehouseId}
                      onChange={(e) => setLinkWarehouseId(e.target.value)}
                      className="h-7 flex-1 rounded border border-input bg-white px-2 text-xs outline-none focus:border-primary"
                    >
                      <option value="">Select warehouse</option>
                      <WarehouseOptionGroups warehouses={warehouseOptions} />
                    </select>
                    <button
                      type="button"
                      disabled={!linkWarehouseId || linkingSaving}
                      onClick={async () => {
                        setLinkingSaving(true);
                        try {
                          // Options are keyed by warehouse location (all share the tenant id)
                          const picked = warehouseOptions.find((w: any) => w.locationId === linkWarehouseId);
                          await api.put(`/payees/${payeeId}`, {
                            vendorType: 'warehouse',
                            linkedWarehouseId: picked?.id ?? null,
                            linkedWarehouseLocationId: picked?.locationId ?? null,
                          });
                          queryClient.invalidateQueries({ queryKey: ['payees'] });
                          setShowLinkWarehouse(false);
                          toast.success('Warehouse linked');
                        } catch {
                          toast.error('Failed to link warehouse');
                        } finally {
                          setLinkingSaving(false);
                        }
                      }}
                      className="h-7 rounded bg-primary px-2.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                      {linkingSaving ? 'Saving…' : 'Link'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowLinkWarehouse(false)}
                      className="h-7 rounded border border-border px-2 text-xs font-medium text-muted-foreground hover:bg-muted"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
            {vendorMissingEmail && (
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2.5">
                <p className="text-xs font-medium text-amber-800">Vendor email required</p>
                <p className="mt-0.5 text-xs text-amber-700">
                  This vendor doesn't have an email on file. Add one to send the PO — we'll save it on the vendor
                  record.
                </p>
                <input
                  type="email"
                  value={vendorEmail}
                  onChange={(e) => {
                    setVendorEmail(e.target.value);
                    if (submitted) setErrors((prev) => ({ ...prev, vendorEmail: '' }));
                  }}
                  placeholder="vendor@example.com"
                  className={`mt-2 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${errors.vendorEmail ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-input focus:border-primary focus:ring-primary'}`}
                />
                {errors.vendorEmail && <p className="mt-1 text-xs text-red-500">{errors.vendorEmail}</p>}
              </div>
            )}
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-foreground">
              Location <span className="text-red-500">*</span>
            </label>
            <select
              value={locationId}
              onChange={(e) => {
                const newLoc = e.target.value;
                setLocationId(newLoc);
                // If the currently selected vendor is now a self-link for this
                // location, clear it so stale state can't survive submit. The
                // banner unmounts naturally when isWarehouseVendor flips false;
                // no need to also clear availabilityResult here.
                const picked = allPayees.find((p: any) => p.id === payeeId);
                if (picked?.linkedWarehouseLocationId && picked.linkedWarehouseLocationId === newLoc) {
                  setPayeeId('');
                }
                if (submitted) setErrors((prev) => ({ ...prev, locationId: newLoc ? '' : 'Location is required' }));
              }}
              className={`mt-1 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${errors.locationId ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-input focus:border-primary focus:ring-primary'}`}
            >
              <option value="">Select location</option>
              {locations.map((l: any) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
            {errors.locationId && <p className="mt-1 text-xs text-red-500">{errors.locationId}</p>}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-foreground">Status</label>
            {status === 'received' || status === 'completed' ? (
              <div className="mt-1 flex h-9 items-center rounded-lg border border-green-200 bg-green-50 px-3 text-sm font-medium text-green-700 capitalize">
                {status}
              </div>
            ) : status === 'fulfilled' ? (
              <div className="mt-1 flex h-9 items-center rounded-lg border border-blue-200 bg-blue-50 px-3 text-sm font-medium text-blue-700">
                Fulfilled
              </div>
            ) : status === 'cancelled' ? (
              <div className="mt-1 flex h-9 items-center rounded-lg border border-amber-200 bg-amber-50 px-3 text-sm font-medium text-amber-700">
                Cancelled
              </div>
            ) : (
              <div className="mt-1 flex h-9 items-center rounded-lg border border-input bg-muted/40 px-3 text-sm font-medium text-foreground">
                {status === 'sent' ? 'Sent' : 'Draft'}
              </div>
            )}
            {status === 'draft' && (
              <p className="mt-1 text-xs text-muted-foreground">
                {initialData
                  ? 'Use the Send button on the PO list to notify the vendor.'
                  : 'This PO will save as a draft. Send to the vendor from the list.'}
              </p>
            )}
            {status === 'sent' && initialData && (
              <p className="mt-1 text-xs text-muted-foreground">
                Re-send the email after edits, or Cancel to notify the vendor.
              </p>
            )}
          </div>

          {/* Purpose — free-text subheading shown in the PO email subject so
              vendors can distinguish multiple POs at a glance. */}
          <div>
            <label className="block text-sm font-medium text-foreground">
              Purpose <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={purpose}
              maxLength={255}
              onChange={(e) => {
                setPurpose(e.target.value);
                if (submitted) setErrors((prev) => ({ ...prev, purpose: e.target.value.trim() ? '' : prev.purpose }));
              }}
              placeholder="e.g. PRODUCE, SWEETS"
              className={`mt-1 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${errors.purpose ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-input focus:border-primary focus:ring-primary'}`}
            />
            {errors.purpose ? (
              <p className="mt-1 text-xs text-red-500">{errors.purpose}</p>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">Appears in the PO email subject.</p>
            )}
          </div>

          {/* Created By */}
          <div>
            <label className="block text-sm font-medium text-foreground">Created By</label>
            <input
              type="text"
              value={createdByName}
              readOnly
              placeholder="Your name"
              className="mt-1 h-9 w-full rounded-lg border border-input bg-muted/40 px-3 text-sm text-muted-foreground outline-none cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-muted-foreground">Created {new Date(createdAt).toLocaleString()}</p>
          </div>

          {/* Invoice # */}
          <div>
            <label className="block text-sm font-medium text-foreground">Vendor Invoice #</label>
            <input
              type="text"
              value={payeeInvoiceNo}
              onChange={(e) => setPayeeInvoiceNo(e.target.value)}
              placeholder={initialData ? 'e.g. INV-2024-001' : 'Auto-generated if blank'}
              className="mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            {!initialData && !payeeInvoiceNo && (
              <p className="mt-1 text-xs text-muted-foreground">
                Will auto-generate as PO-YYYYMMDD-XXXX for reference.
              </p>
            )}
          </div>

          {/* Payment Due Date */}
          <div>
            <label className="block text-sm font-medium text-foreground">Payment Due Date</label>
            <DateInput
              value={paymentDueDate}
              onChange={(e) => setPaymentDueDate(e.target.value)}
              className="mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Received Date (read-only — set automatically when received) */}
          {receivedDate && (
            <div>
              <label className="block text-sm font-medium text-foreground">Received Date</label>
              <div className="mt-1 flex h-9 items-center rounded-lg border border-green-200 bg-green-50 px-3 text-sm text-green-700">
                {receivedDate}
              </div>
            </div>
          )}

          {/* Toggles */}
          <div className="flex items-end gap-6">
            <label className="flex items-center gap-2">
              <button
                type="button"
                disabled={paidToggleSaving || !(statusLocked ? canEditPo : canSavePo)}
                onClick={async () => {
                  const newVal = !orderPaid;
                  if (statusLocked && initialData?.id) {
                    setPaidToggleSaving(true);
                    try {
                      await api.put(`/purchase-orders/${initialData.id}`, { orderPaid: newVal });
                      setOrderPaid(newVal);
                      if (orderReceived) setStatus(newVal ? 'completed' : 'received');
                      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
                    } catch {
                      toast.error('Failed to update paid status');
                    } finally {
                      setPaidToggleSaving(false);
                    }
                  } else {
                    setOrderPaid(newVal);
                  }
                }}
                className={`relative h-6 w-11 rounded-full transition-colors ${orderPaid ? 'bg-primary' : 'bg-gray-200'} ${paidToggleSaving ? 'opacity-50' : ''}`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${orderPaid ? 'translate-x-5' : ''}`}
                />
              </button>
              <span className="text-sm text-foreground">Paid</span>
            </label>
            {orderReceived && (
              <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700">
                ✓ Received
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="sm:col-span-2 lg:col-span-3">
            <label className="block text-sm font-medium text-foreground">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Internal notes about this order..."
              className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      {/* Line Items Section */}
      <div className="relative rounded-lg border border-border bg-white p-6">
        {locationSwitching && (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-white/80 backdrop-blur-[1px]"
            aria-live="polite"
          >
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <p className="mt-2 text-sm text-muted-foreground">Switching location — reloading vendors and products…</p>
          </div>
        )}
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold">Line Items</h3>
          <div className="text-sm text-muted-foreground">
            {items.length} item{items.length !== 1 ? 's' : ''}
            {totalEach > 0 && (
              <>
                {' '}
                &middot; <span className="font-semibold text-foreground">{totalEach}</span> each
              </>
            )}
            {showCostColumns && canSeeCost && (
              <>
                {' '}
                &middot; Total: <span className="font-semibold text-foreground">{formatCurrency(totalCost)}</span>
              </>
            )}
          </div>
        </div>

        {/* Add Product search + Add Multiple — disabled on locked POs so the
            user can't queue up items they'd then fail to save. */}
        <div className={`mb-4 flex items-center gap-3 ${isLocked ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search products by name, barcode, or SKU..."
              value={productSearch}
              disabled={isLocked}
              onChange={(e) => {
                setProductSearch(e.target.value);
                setShowProductDropdown(true);
              }}
              onKeyDown={productScan.onKeyDown}
              onFocus={() => setShowProductDropdown(true)}
              onBlur={() => {
                productScan.onBlur();
                setTimeout(() => setShowProductDropdown(false), 200);
              }}
              className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            {showProductDropdown && filteredProducts.length > 0 && (
              <div className="absolute top-full left-0 z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-border bg-white shadow-lg">
                {filteredProducts.map((product: any) => (
                  <button
                    key={product.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      addProduct(product);
                    }}
                    className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-muted"
                  >
                    <div className="flex-1">
                      <div className="font-medium">{product.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {product.primaryBarcode && `UPC: ${product.primaryBarcode}`}
                        {product.primaryBarcode && product.sku && ' · '}
                        {product.sku && `SKU: ${product.sku}`}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {showCostColumns && canSeeCost && <>Cost: {formatCurrency(product.cost)} · </>}
                      Stock: {product.inStockQty}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            disabled={!payeeId || !locationId || isLocked}
            onClick={() => setShowPickerModal(true)}
            className="inline-flex h-9 min-w-40 shrink-0 items-center justify-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 text-sm font-medium text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ListPlus className="h-4 w-4" />
            Add Multiple
          </button>
        </div>

        {/* Warehouse availability banner — single always-mounted wrapper so
             the section's height doesn't flip when the check transitions from
             pending → settled, or when payee/qty changes trigger a re-check.
             Three visual states share the same outer element:
               1. settled-available     → green
               2. settled-unavailable   → red (grows for the bulleted list)
               3. checking / debouncing → neutral blue (no prior result)
             A re-check after a prior result dims the result + overlays the
             spinner instead of swapping into the neutral state. The legacy
             dual-render (standalone "Checking…" row + separate result
             banner) is gone; that's what was causing the height pop. */}
        {isWarehouseVendor &&
          items.length > 0 &&
          (() => {
            const hasSettledResult = !!visibleAvailabilityResult && visibleAvailabilityResult.items.length > 0;
            const stateClasses = !hasSettledResult
              ? 'border-blue-200 bg-blue-50 text-blue-700'
              : visibleAvailabilityResult!.available
                ? 'border-green-200 bg-green-50 text-green-700'
                : 'border-red-200 bg-red-50 text-red-700';
            return (
              <div
                className={`relative mb-4 flex min-h-[52px] items-center gap-2 rounded-lg border px-4 py-3 text-sm transition-colors transition-opacity ${
                  checkAvailability.isPending && hasSettledResult ? 'opacity-60' : ''
                } ${stateClasses}`}
                aria-live="polite"
              >
                {/* Spinner: always in the DOM so its mount/unmount can't affect
                   layout — opacity toggles based on actual fetch state. */}
                <div
                  aria-hidden={!checkAvailability.isPending}
                  aria-label={checkAvailability.isPending ? 'Checking warehouse availability' : undefined}
                  className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent transition-opacity ${
                    checkAvailability.isPending ? 'opacity-70' : 'opacity-0'
                  }`}
                />
                {!hasSettledResult ? (
                  <span>Checking warehouse availability…</span>
                ) : visibleAvailabilityResult!.available ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    <span>All items available in warehouse stock</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <div>
                      <span className="font-medium">Some items can't be ordered from this warehouse:</span>
                      <ul className="mt-1 list-inside list-disc text-xs">
                        {visibleAvailabilityResult!.items
                          .filter((i: any) => !i.sufficient)
                          .map((i: any) => {
                            const unit = i.warehouseSellingUnit ? ` ${i.warehouseSellingUnit}` : '';
                            let detail: string;
                            if (i.reason === 'not_stocked') {
                              detail = 'not stocked by this warehouse';
                            } else if (i.reason === 'missing_conversion') {
                              // The API attached a conversion-specific message
                              // naming the missing unit + product — surface it
                              // verbatim so the buyer knows exactly which
                              // productUnits row to add on the warehouse-side
                              // product. Falls back to a plain hint if the
                              // backend is older.
                              detail = i.conversionError || 'warehouse missing the unit conversion for this PO unit';
                            } else {
                              detail = `requested ${i.requestedQty}${unit}, available ${i.availableQty}${unit}`;
                            }
                            return (
                              <li key={i.productId}>
                                {i.productName}: {detail}
                              </li>
                            );
                          })}
                      </ul>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

        {/* Line items error */}
        {errors.items && <p className="mb-3 text-xs text-red-500">{errors.items}</p>}

        {/* Block-level summary when one or more lines are missing a unit conversion.
             Submit is also disabled in that state — see the Save button. */}
        {hasMissingConversions && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <div className="font-medium">
              {linesNeedingConversion.length === 1
                ? '1 line needs a unit conversion before this PO can be saved.'
                : `${linesNeedingConversion.length} lines need a unit conversion before this PO can be saved.`}
            </div>
            <ul className="mt-1 list-inside list-disc text-xs">
              {linesNeedingConversion.map(({ item }) => (
                <li key={`${item.productId}-${item.unitOfMeasure}`}>
                  <span className="font-medium">{item.productName}</span> — set how many "each" are in 1{' '}
                  {item.unitOfMeasure} (use the ⚙️ next to the line, or{' '}
                  <a
                    href={`#/pos-management/products/${item.productId}/edit`}
                    target="_blank"
                    rel="noreferrer"
                    className="underline hover:text-red-900"
                  >
                    open the product
                  </a>
                  ).
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Items table */}
        {items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Product</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground w-28">Unit</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground w-40">Qty</th>
                  {showCostColumns && canSeeCost && (
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground w-28">Cost/Unit</th>
                  )}
                  {showCostColumns && canSeeCost && (
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground w-28">Line Total</th>
                  )}
                  {isWarehouseVendor && visibleAvailabilityResult && visibleAvailabilityResult.items.length > 0 && (
                    <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground w-16">Stock</th>
                  )}
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const lineTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.costPerUnit) || 0);
                  return (
                    <tr key={index} className="border-b border-border last:border-0">
                      <td className="px-3 py-2">
                        <div className={`text-sm font-medium ${isZeroQty(item.quantity) ? ZERO_QTY_ROW_CLASS : ''}`}>
                          {item.productName}
                          {isZeroQty(item.quantity) && <ZeroQtyTag />}
                        </div>
                        {item.productBarcode && (
                          <div className="text-xs text-muted-foreground">{item.productBarcode}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {isLocked ? (
                          <span className="text-sm">{item.unitOfMeasure}</span>
                        ) : (
                          <select
                            value={item.unitOfMeasure}
                            onChange={(e) => updateItem(index, 'unitOfMeasure', e.target.value)}
                            className="h-8 w-full rounded border border-input px-2 text-sm"
                          >
                            {unitOptionsFor(item.unitOfMeasure).map((u) => (
                              <option key={u} value={u}>
                                {u}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {isLocked ? (
                          <div className={`text-right text-sm ${isZeroQty(item.quantity) ? ZERO_QTY_ROW_CLASS : ''}`}>
                            {item.quantity}
                          </div>
                        ) : (
                          <input
                            type="number"
                            step={item.soldByWeight ? '0.001' : '1'}
                            min={item.soldByWeight ? '0.001' : '1'}
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                            className="h-8 w-full rounded border border-input px-2 text-right text-sm outline-none focus:border-primary"
                          />
                        )}
                        {item.unitOfMeasure.toLowerCase() !== 'each' &&
                          (() => {
                            // Two-row stack:
                            //   1) A prominent "= N each" label (the *result* of the
                            //      conversion — what reviewers want to see at a glance).
                            //   2) A subtler click-to-edit chip showing the *ratio*
                            //      (1 case = 5 each) that opens the right drawer based
                            //      on PO status. Always rendered so the math is never
                            //      hidden behind a tiny CTA.
                            const baseQty = parseFloat(item.baseQty) || 1;
                            const hasConversion = baseQty > 1;
                            const qty = parseFloat(item.quantity) || 0;
                            const total = Math.round(qty * baseQty);
                            // Fixing a received line's conversion is a PO edit.
                            const onClick = () => {
                              if (orderReceived && item.id) {
                                setAdjustFor({
                                  lineId: item.id,
                                  productId: item.productId,
                                  productName: item.productName,
                                  unitOfMeasure: item.unitOfMeasure,
                                  quantity: qty,
                                  oldBaseQty: baseQty,
                                  itemIndex: index,
                                });
                              } else {
                                setFixUnitFor({
                                  productId: item.productId,
                                  productName: item.productName,
                                  unitOfMeasure: item.unitOfMeasure,
                                  currentBaseQty: hasConversion ? baseQty : null,
                                  itemIndex: index,
                                });
                              }
                            };
                            // Inline math + bare settings icon. Same shape regardless of
                            // whether a conversion is configured (1:1 looks identical,
                            // just multiplies by 1) — no warning text, no chip.
                            return (
                              <div
                                className={`mt-1 flex flex-wrap items-center justify-end gap-x-1.5 gap-y-0.5 text-xs leading-tight ${
                                  hasConversion ? 'text-muted-foreground' : 'text-red-700'
                                }`}
                              >
                                {/* Format: "<qty> <unit> × <baseQty> = <total> each".
                                     The middle number is the conversion (each per
                                     purchasing unit), so when qty=1 baseQty=10
                                     this reads "1 case × 10 = 10 each". whitespace-nowrap
                                     on the inner span so the math itself doesn't
                                     break across lines mid-equation. */}
                                <span className="whitespace-nowrap">
                                  {qty} {item.unitOfMeasure} × {baseQty} ={' '}
                                  <span className={hasConversion ? 'font-semibold text-foreground' : 'font-semibold'}>
                                    {total}
                                  </span>{' '}
                                  each
                                </span>
                                <button
                                  type="button"
                                  onClick={onClick}
                                  disabled={orderReceived && !!item.id && !canEditPo}
                                  title={
                                    orderReceived
                                      ? 'Adjust conversion (will recompute stock)'
                                      : hasConversion
                                        ? 'Edit conversion'
                                        : 'Set conversion'
                                  }
                                  aria-label={hasConversion ? 'Edit conversion' : 'Set conversion'}
                                  className={`rounded p-0.5 hover:bg-muted ${
                                    hasConversion
                                      ? 'text-muted-foreground hover:text-foreground'
                                      : 'text-red-700 hover:text-red-900'
                                  }`}
                                >
                                  <Settings className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            );
                          })()}
                      </td>
                      {showCostColumns && canSeeCost && (
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.costPerUnit}
                            onChange={(e) => updateItem(index, 'costPerUnit', e.target.value)}
                            className="h-8 w-full rounded border border-input px-2 text-right text-sm outline-none focus:border-primary"
                          />
                        </td>
                      )}
                      {showCostColumns && canSeeCost && (
                        <td className="px-3 py-2 text-right text-sm font-medium">{formatCurrency(lineTotal)}</td>
                      )}
                      {isWarehouseVendor && visibleAvailabilityResult && visibleAvailabilityResult.items.length > 0 && (
                        <td className="px-3 py-2">
                          {(() => {
                            const avail = visibleAvailabilityResult.items?.find(
                              (a: any) => a.productId === item.productId,
                            );
                            if (!avail) return <span className="text-xs text-muted-foreground">—</span>;
                            if (avail.sufficient) {
                              return (
                                <CheckCircle2
                                  className="h-4 w-4 text-green-500"
                                  aria-label={`Available: ${avail.availableQty}`}
                                />
                              );
                            }
                            return (
                              <span
                                className="flex items-center gap-1 text-xs text-red-500"
                                aria-label={`Available: ${avail.availableQty}, Requested: ${avail.requestedQty}`}
                              >
                                <AlertTriangle className="h-4 w-4" />
                                {avail.availableQty}
                              </span>
                            );
                          })()}
                        </td>
                      )}
                      <td className="px-3 py-2">
                        {!isLocked && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded border border-border text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {showCostColumns && canSeeCost && (
                <tfoot>
                  <tr className="border-t-2 border-border">
                    <td colSpan={5} className="px-3 py-3 text-right text-sm font-semibold">
                      Total Cost
                    </td>
                    <td className="px-3 py-3 text-right text-sm font-bold text-primary">{formatCurrency(totalCost)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ) : (
          <div className="flex h-32 items-center justify-center rounded-lg border-2 border-dashed border-border text-sm text-muted-foreground">
            <div className="text-center">
              <Plus className="mx-auto h-8 w-8 text-muted-foreground/50" />
              <p className="mt-2">
                {!payeeId || !locationId ? (
                  <span>Select a vendor and location above to add products</span>
                ) : (
                  <>
                    Search products above or use{' '}
                    <button
                      type="button"
                      onClick={() => setShowPickerModal(true)}
                      className="font-medium text-primary hover:underline"
                    >
                      Add Multiple
                    </button>{' '}
                    to bulk-select
                  </>
                )}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Locked banner — terminal statuses cannot be edited (received,
          fulfilled, cancelled). The API also rejects PUT in those statuses. */}
      {!statusLocked && !canSavePo && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="font-medium">You can view this purchase order but not change it.</span>
        </div>
      )}
      {statusLocked && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="font-medium">This purchase order is {status} and can no longer be edited.</span>
          {(status === 'received' || status === 'completed') &&
            ' Stock has already been incremented at the destination location.'}
          {status === 'fulfilled' && ' Warehouse stock has already been deducted.'}
          {status === 'cancelled' && ' The vendor has been notified that this order is cancelled.'}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 border-t border-border pt-4">
        <a
          href="#/pos-management?tab=purchase-orders"
          className="inline-flex h-9 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
        >
          {isLocked ? 'Back' : 'Cancel'}
        </a>
        {!isLocked && (
          <button
            type="submit"
            disabled={
              isSubmitting ||
              hasMissingConversions ||
              hasRequiredMissing ||
              (isWarehouseVendor &&
                visibleAvailabilityResult &&
                visibleAvailabilityResult.items.length > 0 &&
                !visibleAvailabilityResult.available)
            }
            title={
              hasRequiredMissing
                ? `Fill in: ${requiredMissing.join(', ')}.`
                : hasMissingConversions
                  ? `Set the unit conversion on ${linesNeedingConversion.length} line${
                      linesNeedingConversion.length === 1 ? '' : 's'
                    } before saving.`
                  : undefined
            }
            className="inline-flex h-9 items-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : initialData ? 'Update Order' : 'Save as Draft'}
          </button>
        )}
      </div>

      {/* Product Picker Modal */}
      {showPickerModal && (
        <ProductPickerModal
          payeeId={payeeId}
          locationId={locationId}
          existingProductIds={existingProductIds}
          onAdd={addMultipleProducts}
          onClose={() => setShowPickerModal(false)}
        />
      )}
      {showAddVendorModal &&
        createPortal(
          <AddVendorModal
            locationId={locationId || undefined}
            onClose={() => setShowAddVendorModal(false)}
            onCreated={(newPayeeId) => {
              setPayeeId(newPayeeId);
              if (submitted) setErrors((prev) => ({ ...prev, payeeId: '' }));
            }}
          />,
          document.body,
        )}
      {fixUnitFor &&
        createPortal(
          <FixUnitDrawer
            productId={fixUnitFor.productId}
            productName={fixUnitFor.productName}
            unitOfMeasure={fixUnitFor.unitOfMeasure}
            currentBaseQty={fixUnitFor.currentBaseQty}
            onClose={() => setFixUnitFor(null)}
            onSaved={(newBaseQty) => {
              // Update the line item's baseQty in-place so the form reflects
              // the new conversion immediately (the picker query will refetch
              // in the background via useUpdateProduct's onSuccess).
              const idx = fixUnitFor.itemIndex;
              setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, baseQty: String(newBaseQty) } : it)));
              setFixUnitFor(null);
              toast.success(`Conversion saved: 1 ${fixUnitFor.unitOfMeasure} = ${newBaseQty} each`);
            }}
          />,
          document.body,
        )}
      {adjustFor &&
        initialData?.id &&
        createPortal(
          <AdjustConversionDrawer
            poId={initialData.id}
            line={adjustFor}
            onClose={() => setAdjustFor(null)}
            onSaved={(newBaseQty) => {
              const idx = adjustFor.itemIndex;
              setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, baseQty: String(newBaseQty) } : it)));
              setAdjustFor(null);
            }}
          />,
          document.body,
        )}
    </form>
  );
}

// ── Fix Unit Drawer ──
// Opens when a PO line uses a unit the product doesn't have a productUnits row
// for. Adds (or updates) a row on the product so future POs resolve correctly,
// and patches the line item's local baseQty so the form preview is accurate.

function FixUnitDrawer({
  productId,
  productName,
  unitOfMeasure,
  currentBaseQty,
  onClose,
  onSaved,
}: {
  productId: string;
  productName: string;
  unitOfMeasure: string;
  currentBaseQty: number | null;
  onClose: () => void;
  onSaved: (baseQty: number) => void;
}) {
  // Always fetch the product on open to look up the most-current units[] row.
  // Without this we'd trust whatever the form picker passed in — which can be
  // stale (newer save in another tab) or missing (line was added before the
  // picker started returning units), and end up clobbering a real value with
  // the user's manual entry.
  const [baseQty, setBaseQty] = useState(currentBaseQty != null && currentBaseQty > 0 ? String(currentBaseQty) : '');
  const [productLoadedBaseQty, setProductLoadedBaseQty] = useState<number | null>(currentBaseQty);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const updateProduct = useUpdateProduct();
  // A conversion is saved on the product, so it needs Edit products.
  const canEditProducts = usePermissions().has('products.edit');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const productRes = await productsApiGet(productId);
        const existingUnits: Array<{ unitName: string; baseQty: number | string }> = productRes?.units ?? [];
        const norm = (s: string) => {
          const lower = (s || '').toLowerCase().trim();
          return lower.endsWith('s') ? lower.slice(0, -1) : lower;
        };
        const target = norm(unitOfMeasure);
        const match = existingUnits.find((u) => norm(u.unitName) === target);
        if (cancelled) return;
        const matchBaseQty = match ? Number(match.baseQty) : null;
        if (matchBaseQty != null && matchBaseQty > 0) {
          setProductLoadedBaseQty(matchBaseQty);
          // Only overwrite the input if the user hasn't typed a value yet.
          // Avoids stomping mid-edit if the fetch resolves late.
          setBaseQty((prev) => (prev ? prev : String(matchBaseQty)));
        }
      } finally {
        if (!cancelled) setLoadingProduct(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productId, unitOfMeasure]);

  const isEdit = productLoadedBaseQty != null && productLoadedBaseQty > 0;
  const numeric = parseFloat(baseQty);
  const valid = isFinite(numeric) && numeric > 0;

  const save = async () => {
    setSubmitted(true);
    if (!valid) return;
    try {
      // Fetch current product to merge a new units[] row without losing existing ones.
      const productRes = await productsApiGet(productId);
      const existingUnits: Array<{ unitName: string; baseQty: number | string }> = productRes?.units ?? [];
      const cleanedExisting = existingUnits
        .filter((u) => u.unitName?.toLowerCase() !== unitOfMeasure.toLowerCase())
        .map((u) => ({
          unitName: u.unitName,
          baseQty: typeof u.baseQty === 'number' ? u.baseQty : parseFloat(String(u.baseQty)),
          ...(u as any),
        }));
      const newUnits = [...cleanedExisting, { unitName: unitOfMeasure, baseQty: numeric }];
      await updateProduct.mutateAsync({ id: productId, units: newUnits });
      onSaved(numeric);
    } catch (err: any) {
      toast.error(err?.error?.message ?? 'Failed to save conversion');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end sm:items-center sm:justify-center" role="dialog">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-t-xl bg-white p-6 shadow-xl sm:rounded-xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">{isEdit ? 'Edit conversion' : 'Set conversion'}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{productName}</span>
              {loadingProduct ? (
                <> — loading current conversion…</>
              ) : isEdit ? (
                <>
                  {' '}
                  is set to{' '}
                  <span className="font-medium text-foreground">
                    1 {unitOfMeasure} = {productLoadedBaseQty} each
                  </span>
                  . Update it below — saves to the product so future POs and the cost calculator use the new value.
                </>
              ) : (
                <>
                  {' '}
                  doesn't have a conversion for <span className="font-medium text-foreground">
                    {unitOfMeasure}
                  </span>{' '}
                  yet. Tell us how many "each" are in one {unitOfMeasure} so receiving credits stock correctly.
                </>
              )}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        <label className="block">
          <span className="text-sm font-medium">1 {unitOfMeasure} =</span>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="number"
              step="0.001"
              min="0.001"
              value={baseQty}
              onChange={(e) => setBaseQty(e.target.value)}
              autoFocus
              className={`h-9 w-32 rounded-lg border bg-white px-3 text-sm outline-none focus:ring-1 ${
                submitted && !valid
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : 'border-input focus:border-primary focus:ring-primary'
              }`}
            />
            <span className="text-sm text-muted-foreground">each</span>
          </div>
          {submitted && !valid && (
            <p className="mt-1 text-xs text-red-500" role="alert">
              Enter a positive number
            </p>
          )}
        </label>

        {!canEditProducts && (
          <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" role="alert">
            You don&apos;t have permission to edit products, so you can&apos;t save this. Ask someone who can edit
            products to set it, then come back to this order.
          </p>
        )}

        <p className="mt-3 text-xs text-muted-foreground">
          Saves to the product. Every future PO and the product's cost calculator will pick up the new value.
        </p>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-input bg-white px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={updateProduct.isPending || !canEditProducts}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {updateProduct.isPending ? 'Saving…' : isEdit ? 'Update conversion' : 'Save conversion'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper that hits the products service for a single product (units included).
// Used by FixUnitDrawer above to merge a new units[] row without dropping existing ones.
async function productsApiGet(productId: string): Promise<any> {
  const { productsApi } = await import('@/lib/api-client');
  const res = await productsApi.get<{ data: any }>(`/products/${productId}`);
  return res.data;
}

// ── Adjust Conversion Drawer (received POs) ──
// Live preview of how the per-location stock changes when baseQty changes,
// then posts to /purchase-orders/:poId/items/:lineId/correct-conversion which
// applies the delta atomically (and refuses if it would push stock negative).

function AdjustConversionDrawer({
  poId,
  line,
  onClose,
  onSaved,
}: {
  poId: string;
  line: {
    lineId: string;
    productId: string;
    productName: string;
    unitOfMeasure: string;
    quantity: number;
    oldBaseQty: number;
  };
  onClose: () => void;
  onSaved: (newBaseQty: number) => void;
}) {
  const [baseQty, setBaseQty] = useState(String(line.oldBaseQty));
  const [alsoUpdateProductUnit, setAlsoUpdateProductUnit] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const correct = useCorrectConversion();

  const numeric = parseFloat(baseQty);
  const valid = isFinite(numeric) && numeric > 0;
  const oldStockApplied = Math.round(line.quantity * line.oldBaseQty);
  const newStockApplied = valid ? Math.round(line.quantity * numeric) : oldStockApplied;
  const delta = newStockApplied - oldStockApplied;

  const save = async () => {
    setSubmitted(true);
    if (!valid || numeric === line.oldBaseQty) return;
    try {
      await correct.mutateAsync({
        poId,
        lineId: line.lineId,
        newBaseQty: numeric,
        alsoUpdateProductUnit,
      });
      toast.success(
        `Conversion updated: ${oldStockApplied} → ${newStockApplied} each (${delta >= 0 ? '+' : ''}${delta})`,
      );
      onSaved(numeric);
    } catch (err: any) {
      toast.error(err?.error?.message ?? 'Failed to adjust conversion');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">Adjust conversion</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Recalculate stock that was credited at receive time for{' '}
              <span className="font-medium text-foreground">{line.productName}</span>.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-3 rounded-lg border border-border bg-muted/30 p-3 text-sm">
          <div className="text-xs text-muted-foreground">Ordered</div>
          <div>
            {line.quantity} {line.unitOfMeasure}
          </div>
        </div>

        <label className="block">
          <span className="text-sm font-medium">1 {line.unitOfMeasure} =</span>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="number"
              step="0.001"
              min="0.001"
              value={baseQty}
              onChange={(e) => setBaseQty(e.target.value)}
              autoFocus
              className={`h-9 w-32 rounded-lg border bg-white px-3 text-sm outline-none focus:ring-1 ${
                submitted && !valid
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : 'border-input focus:border-primary focus:ring-primary'
              }`}
            />
            <span className="text-sm text-muted-foreground">each</span>
            <span className="ml-auto text-xs text-muted-foreground">was {line.oldBaseQty}</span>
          </div>
        </label>

        <div className="mt-4 rounded-lg border border-border p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Stock applied at receive</span>
            <span className="font-medium">{oldStockApplied} each</span>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <span className="text-muted-foreground">Should have been</span>
            <span className="font-medium">{newStockApplied} each</span>
          </div>
          <div
            className={`mt-2 flex items-center justify-between rounded px-2 py-1 ${
              delta === 0 ? 'bg-muted/40' : delta > 0 ? 'bg-green-50 text-green-900' : 'bg-amber-50 text-amber-900'
            }`}
          >
            <span className="text-xs font-medium">Stock delta to apply</span>
            <span className="text-sm font-semibold">
              {delta >= 0 ? '+' : ''}
              {delta} each
            </span>
          </div>
        </div>

        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={alsoUpdateProductUnit}
            onChange={(e) => setAlsoUpdateProductUnit(e.target.checked)}
            className="h-4 w-4"
          />
          <span>
            Also update the product so future POs use 1 {line.unitOfMeasure} = {valid ? numeric : '?'} each
          </span>
        </label>

        {delta < 0 && (
          <p className="mt-3 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
            This is a downward correction. If stock has already been drawn down at this location, the change may be
            refused — do a manual stock adjustment first if so.
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-input bg-white px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!valid || numeric === line.oldBaseQty || correct.isPending}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {correct.isPending ? 'Applying…' : 'Apply correction'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add Vendor Modal ──

const vendorInputBase = 'h-9 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:ring-1';
const vendorInputNormal = `${vendorInputBase} border-input focus:border-primary focus:ring-primary`;
const vendorInputError = `${vendorInputBase} border-red-500 focus:border-red-500 focus:ring-red-500`;

function VendorFieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1 text-xs text-red-500" role="alert">
      {message}
    </p>
  );
}

function vendorFieldCls(fieldErrors: Record<string, string>, field: string): string {
  return fieldErrors[field] ? vendorInputError : vendorInputNormal;
}

// Own warehouses first, then other tenants' private warehouses mapped to the
// selected location (labelled with the owning company). Options are keyed by
// warehouse location id.
function WarehouseOptionGroups({ warehouses }: { warehouses: any[] }) {
  const own = warehouses.filter((w) => w.source !== 'private');
  const shared = warehouses.filter((w) => w.source === 'private');
  const option = (w: any) => (
    <option key={w.locationId} value={w.locationId}>
      {w.name}
      {w.source === 'private' && w.ownerTenantName ? ` (${w.ownerTenantName})` : ''}
    </option>
  );
  if (shared.length === 0) return <>{own.map(option)}</>;
  return (
    <>
      {own.length > 0 && <optgroup label="Your warehouses">{own.map(option)}</optgroup>}
      <optgroup label="Private warehouses">{shared.map(option)}</optgroup>
    </>
  );
}

function AddVendorModal({
  onClose,
  onCreated,
  locationId,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
  locationId?: string;
}) {
  const createPayee = useCreatePayee();
  const { data: warehouseData } = useWarehouses(locationId);
  const warehouses = warehouseData?.data ?? [];

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    vendorType: 'third_party' as 'third_party' | 'warehouse',
    linkedWarehouseId: '',
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [duplicate, setDuplicate] = useState<{
    id: string;
    name: string;
    email: string;
    company: string | null;
  } | null>(null);

  const update = (field: string, value: string) => setFormData((prev) => ({ ...prev, [field]: value }));
  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  const handleAlphaInput = (field: string, value: string) => {
    const filtered = value.replace(/[^a-zA-Z\s]/g, '');
    update(field, filtered);
  };

  const fieldErrors = useMemo<Record<string, string>>(() => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName)) {
      errors.firstName = 'Only alphabets and spaces are allowed';
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName)) {
      errors.lastName = 'Only alphabets and spaces are allowed';
    } else if (formData.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email address';
    }

    {
      const phoneErr = validatePhone(formData.phone);
      if (phoneErr) errors.phone = phoneErr;
    }

    if (!formData.company.trim()) {
      errors.company = 'Company name is required';
    } else if (formData.company.trim().length < 2) {
      errors.company = 'Company name must be at least 2 characters';
    }

    if (formData.vendorType === 'warehouse' && !formData.linkedWarehouseId) {
      errors.linkedWarehouseId = 'Please select a warehouse';
    }

    return errors;
  }, [formData]);

  const isFormValid = Object.keys(fieldErrors).length === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const allTouched: Record<string, boolean> = {};
    Object.keys(formData).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    if (!isFormValid) return;

    // The select holds the warehouse LOCATION id; the link stores the owning tenant id
    // plus the locationId so the server can run location-aware stock checks.
    const pickedWarehouse = warehouses.find((w: any) => w.locationId === formData.linkedWarehouseId);
    const linkedWarehouseLocationId =
      formData.vendorType === 'warehouse' ? (pickedWarehouse?.locationId ?? null) : null;

    setDuplicate(null);
    try {
      const result: any = await createPayee.mutateAsync({
        name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        email: formData.email.trim() || null,
        phone: formData.phone || null,
        company: formData.company.trim() || null,
        vendorType: formData.vendorType,
        linkedWarehouseId: formData.vendorType === 'warehouse' ? (pickedWarehouse?.id ?? null) : null,
        linkedWarehouseLocationId,
      } as any);
      toast.success('Vendor created successfully');
      onCreated(result.data.id);
      onClose();
    } catch (err: any) {
      // Backend returns 409 with code DUPLICATE_VENDOR + the existing record
      // when a vendor with this email already exists in either payees or
      // sp_payees. The api-client throws the body directly as { error: {...} }.
      // Surface it inline so the user can pick the existing one.
      const errBody = err?.error;
      if (errBody?.code === 'DUPLICATE_VENDOR' && errBody.existing) {
        setDuplicate(errBody.existing);
      } else {
        toast.error('Failed to create vendor');
      }
    }
  };

  const useExistingDuplicate = () => {
    if (!duplicate) return;
    onCreated(duplicate.id);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 pt-[10vh] pb-8">
      <div className="relative w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">Add Vendor</h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-muted">
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
        <div className="px-6 py-5">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  value={formData.firstName}
                  onChange={(e) => handleAlphaInput('firstName', e.target.value)}
                  onBlur={() => markTouched('firstName')}
                  placeholder="John"
                  className={`mt-1 ${vendorFieldCls(touched.firstName ? fieldErrors : {}, 'firstName')}`}
                />
                {touched.firstName && <VendorFieldError message={fieldErrors.firstName} />}
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  value={formData.lastName}
                  onChange={(e) => handleAlphaInput('lastName', e.target.value)}
                  onBlur={() => markTouched('lastName')}
                  placeholder="Smith"
                  className={`mt-1 ${vendorFieldCls(touched.lastName ? fieldErrors : {}, 'lastName')}`}
                />
                {touched.lastName && <VendorFieldError message={fieldErrors.lastName} />}
              </div>
            </div>

            {/* Contact Fields */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => update('email', e.target.value)}
                  onBlur={() => markTouched('email')}
                  placeholder="john@example.com"
                  className={`mt-1 ${vendorFieldCls(touched.email ? fieldErrors : {}, 'email')}`}
                />
                {touched.email && <VendorFieldError message={fieldErrors.email} />}
              </div>
              <div>
                <label className="block text-sm font-medium">Phone</label>
                <PhoneInput
                  value={formData.phone}
                  onValueChange={(digits) => update('phone', digits)}
                  onBlur={() => markTouched('phone')}
                  placeholder="123-456-7890"
                  className={`mt-1 ${vendorFieldCls(touched.phone ? fieldErrors : {}, 'phone')}`}
                  aria-invalid={!!fieldErrors.phone}
                />
                {touched.phone && <VendorFieldError message={fieldErrors.phone} />}
              </div>
              <div className="col-span-1 sm:col-span-2">
                <label className="block text-sm font-medium">
                  Company <span className="text-red-500">*</span>
                </label>
                <input
                  value={formData.company}
                  onChange={(e) => update('company', e.target.value)}
                  onBlur={() => markTouched('company')}
                  placeholder="Company name"
                  className={`mt-1 ${vendorFieldCls(touched.company ? fieldErrors : {}, 'company')}`}
                />
                {touched.company && fieldErrors.company ? (
                  <VendorFieldError message={fieldErrors.company} />
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">If not a company, enter your First Name.</p>
                )}
              </div>
            </div>

            {/* Vendor Type */}
            <div>
              <label className="block text-sm font-medium">Vendor Type</label>
              <div className="mt-1 flex gap-3">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="vendorType"
                    value="third_party"
                    checked={formData.vendorType === 'third_party'}
                    onChange={() => update('vendorType', 'third_party')}
                    className="text-primary"
                  />
                  <span className="text-sm">3rd Party</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="vendorType"
                    value="warehouse"
                    checked={formData.vendorType === 'warehouse'}
                    onChange={() => update('vendorType', 'warehouse')}
                    className="text-primary"
                  />
                  <span className="text-sm">Link Warehouse</span>
                </label>
              </div>
            </div>

            {/* Linked Warehouse (only if warehouse vendor) */}
            {formData.vendorType === 'warehouse' && (
              <div>
                <label className="block text-sm font-medium">
                  Linked Warehouse <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.linkedWarehouseId}
                  onChange={(e) => update('linkedWarehouseId', e.target.value)}
                  onBlur={() => markTouched('linkedWarehouseId')}
                  className={`mt-1 ${vendorFieldCls(touched.linkedWarehouseId ? fieldErrors : {}, 'linkedWarehouseId')}`}
                >
                  <option value="">Select warehouse</option>
                  <WarehouseOptionGroups warehouses={warehouses} />
                </select>
                {touched.linkedWarehouseId && <VendorFieldError message={fieldErrors.linkedWarehouseId} />}
                {warehouses.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600">
                    No warehouses found. Create a warehouse first via Add Company / Warehouse.
                  </p>
                )}
              </div>
            )}

            {duplicate && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-900">A vendor with this email already exists</p>
                <p className="mt-1 text-xs text-amber-800">
                  <span className="font-semibold">{duplicate.name}</span>
                  {duplicate.company ? ` (${duplicate.company})` : ''} · {duplicate.email}
                </p>
                <button
                  type="button"
                  onClick={useExistingDuplicate}
                  className="mt-2 inline-flex h-8 items-center rounded-md bg-amber-600 px-3 text-xs font-medium text-white hover:bg-amber-700"
                >
                  Use existing vendor
                </button>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isFormValid || createPayee.isPending}
                className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {createPayee.isPending ? 'Creating...' : 'Create Vendor'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
